import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { isAfter } from 'date-fns';
import { isEmpty } from 'ramda';
import { useDebounce } from 'react-use';

import { addError, addWarning } from 'actions/Error';
import { previousModalPage } from 'actions/modalPage';
import { useShortcut } from 'utils/hooks/keyboard/useShortcut';

import {
  addLineBreaksToCode,
  extractInfo,
  getAge,
  getTimeTillExpiry,
} from '../utils';

const CustomerInfoCard = ({ data, deny, approve, loading }) => {
  if (loading)
    return (
      <Box
        style={{
          display: 'flex',
          flex: '1 1 0',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (isEmpty(data)) return null;
  return (
    <>
      <Grid item xs={12}>
        <Typography
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Legal Name:</span>
          <span>{`${data.firstName} ${data.middleName ?? ''} ${
            data.lastName
          }`}</span>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Age:</span>
          <span>{`${getAge(data.dateOfBirth)}`}</span>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <span>ID Expiration:</span>
          <span>{getTimeTillExpiry(data.dateOfExpiry)}</span>
        </Typography>
      </Grid>
      <Grid item xs={12} style={{ display: 'flex' }}>
        <Button
          style={{ flex: '1 1 0', marginRight: '0.5em' }}
          variant="outlined"
          color="primary"
          onClick={deny}
        >
          Deny
        </Button>
        <Button
          style={{ flex: '1 1 0', marginLeft: '0.5em' }}
          variant="outlined"
          color="secondary"
          onClick={approve}
        >
          Approve
        </Button>
      </Grid>
    </>
  );
};

const SCAN_FOCUS_STATES = {
  awaitingScan: {
    color: 'green',
    message: 'Please scan customers driving licence',
  },
  lostFocus: {
    color: 'red',
    message:
      'Focus lost. Please, press REFOCUS button to refocus before you proceed with the scan',
  },
  receivingData: {
    color: 'lightblue',
    message:
      'Receiving data from scanner. Do not type nor click anywhere until the process is finished.',
  },
  verifyData: { color: 'green', message: 'Verify driving license data' },
};

const USDLAgeVerificationModal = ({ resolve, reject, minAge = 21 }) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLTextAreaElement>(null);

  const refocusInput = () => {
    ref?.current?.focus?.();
  };

  const [value, setValue] = useState('');
  const [data, setData] = useState<Record<string, string>>({});

  const close = () => {
    dispatch(previousModalPage());
  };

  useShortcut('openPayment', refocusInput, 100);
  useShortcut('confirmPayments', refocusInput, 100);
  useShortcut('searchProducts', refocusInput, 100);

  useDebounce(
    () => {
      if (!value) return;
      const data = extractInfo(addLineBreaksToCode(value));
      if (
        isEmpty(data) ||
        !(
          data.dateOfBirth &&
          data.dateOfExpiry &&
          data.firstName &&
          data.lastName
        )
      ) {
        dispatch(addWarning('Scan failed. Please try scanning one more time.'));
        setValue('');
        refocusInput();
      }
      // check expiration
      else if (
        isAfter(
          new Date(),
          new Date(
            Number(data.dateOfExpiry.slice(-4)),
            Number(data.dateOfExpiry.slice(0, 2)) - 1,
          ),
        )
      ) {
        dispatch(addError('License has expired'));
        close();
        reject();
      }
      // check age
      else if (getAge(data.dateOfBirth) < minAge) {
        dispatch(addError('Client is under the required age for this product'));
        close();
        reject();
      } else {
        setValue('');
        setData(data);
      }
    },
    1000,
    [value],
  );

  const scanState = (() => {
    if (isEmpty(data)) {
      if (document.activeElement?.id !== ref?.current?.id) {
        return SCAN_FOCUS_STATES.lostFocus;
      }
      if (!value.length) {
        return SCAN_FOCUS_STATES.awaitingScan;
      }
      return SCAN_FOCUS_STATES.receivingData;
    }
    return SCAN_FOCUS_STATES.verifyData;
  })();

  return (
    <Paper
      onClick={refocusInput}
      style={{
        padding: '2em',
        minHeight: 300,
        display: 'flex',
      }}
    >
      <Grid container spacing={2} style={{ display: 'flex', flex: '1 1 0' }}>
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <Typography variant="h4">USDL Age Verification</Typography>
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <Typography style={{ color: scanState.color, fontSize: '1.5rem' }}>
            {scanState.message}
          </Typography>
        </Grid>
        <CustomerInfoCard
          data={data}
          loading={!!value}
          deny={() => {
            reject();
            close();
          }}
          approve={() => {
            resolve(getAge(data.dateOfBirth));
            close();
          }}
        />
        {scanState === SCAN_FOCUS_STATES.lostFocus && (
          <Grid item xs={12} style={{ marginTop: 'auto' }}>
            <Button variant="outlined" fullWidth onClick={refocusInput}>
              Refocus
            </Button>
          </Grid>
        )}
        <Grid item xs={12} style={{ marginTop: 'auto' }}>
          <Button variant="outlined" fullWidth onClick={close}>
            Cancel
          </Button>
        </Grid>
        <textarea
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          id="usdl-hidden-input"
          ref={ref}
          value={value}
          style={{
            width: 0,
            height: 0,
            padding: 0,
            margin: 0,
            position: 'fixed',
            top: -20,
          }}
          onChange={e => setValue(e.target.value)}
        />
      </Grid>
    </Paper>
  );
};

export default USDLAgeVerificationModal;
